import { useEffect, useRef, useState } from "react";
import TagBadge from "./TagBadge";
import { addTag, removeTag } from "../lib/tags";

interface Props {
  cardName: string;
  initialTags: string[];
  /** 既存の全タグ(候補表示用) */
  knownTags: string[];
  onSave: (tags: string[]) => Promise<void>;
  onClose: () => void;
}

export default function TagEditor({
  cardName,
  initialTags,
  knownTags,
  onSave,
  onClose,
}: Props) {
  const [tags, setTags] = useState<string[]>(initialTags);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submitInput = () => {
    const t = input.trim();
    if (!t) return;
    setTags((prev) => addTag(prev, t));
    setInput("");
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(tags);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const suggestions = knownTags.filter(
    (t) => !tags.includes(t) && (input === "" || t.includes(input.trim())),
  ).slice(0, 12);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="serif text-lg font-medium text-navy">
          タグを編集
        </h3>
        <p className="mt-1 text-xs text-inkLight">{cardName}</p>

        <div className="mt-5">
          <label className="mb-2 block text-xs font-medium text-navySoft">
            付与中のタグ
          </label>
          <div className="flex min-h-[2rem] flex-wrap gap-2 rounded-md border border-washiDeep bg-washi p-2">
            {tags.length === 0 && (
              <span className="text-xs text-inkLight">まだタグがありません</span>
            )}
            {tags.map((t) => (
              <TagBadge
                key={t}
                tag={t}
                size="md"
                onRemove={() => setTags((prev) => removeTag(prev, t))}
              />
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-2 block text-xs font-medium text-navySoft">
            タグを追加(Enter で確定)
          </label>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitInput();
              }
            }}
            placeholder="例: 重要, 取引先, 営業"
            className="w-full rounded-md border border-washiDeep bg-white px-3 py-2 text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy/30"
          />
        </div>

        {suggestions.length > 0 && (
          <div className="mt-3">
            <p className="mb-2 text-xs text-inkLight">候補(クリックで追加)</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((t) => (
                <TagBadge
                  key={t}
                  tag={t}
                  size="sm"
                  onClick={() => setTags((prev) => addTag(prev, t))}
                />
              ))}
            </div>
          </div>
        )}

        {error && (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={saving}
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary"
            disabled={saving}
          >
            {saving ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}
